import React from "react";
import { motion } from "framer-motion";
import {
  PlayCircle,
  Library,
  PhoneCall,
  FlaskConical,
  Users,
} from "lucide-react";

const steps = [
  {
    number: "1",
    today: true,
    icon: PlayCircle,
    color: "#3b82f6",
    tint: "blue",
    title: "Learn about your office + live product demos",
    text: "We start by understanding your practice, your team, and your goals — then you see the platform in action today: real workflows, real AI, real data.",
  },
  {
    number: "2",
    icon: Library,
    color: "#6366f1",
    tint: "indigo",
    title: "Take-home decks & learning library",
    text: "We send you home with detailed decks and a learning library of in-depth walkthroughs, so you can dig in on your own time and share with partners.",
  },
  {
    number: "3",
    icon: PhoneCall,
    color: "#7c3aed",
    tint: "violet",
    title: "2nd call — deep dive & Q&A",
    text: "We meet again for a full product deep-dive and answer every question your team brings.",
  },
  {
    number: "4",
    icon: FlaskConical,
    color: "#a855f7",
    tint: "purple",
    title: "Sandbox access",
    text: "We give you a sandbox to take home and try Asha yourself — at your own pace, no pressure.",
  },
  {
    number: "5",
    icon: Users,
    color: "#d946ef",
    tint: "fuchsia",
    title: "Talk to a dentist KOL",
    text: "We arrange a call with one of our dentist KOLs who actively uses Asha in their office, so you can learn firsthand how offices are using Asha.",
  },
];

const DeRiskProcessSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-violet-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1120px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Header */}
        <div className="text-center mb-7 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-[2.6rem] font-extrabold text-slate-900 tracking-tight leading-[1.05]"
          >
            Our &ldquo;match&rdquo; process,{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-fuchsia-600 bg-clip-text text-transparent">
              how we de-risk Asha for your office.
            </span>
          </motion.h2>
        </div>

        {/* Vertical timeline */}
        <div className="relative max-w-[920px] mx-auto w-full">
          {/* The gradient connector line — sits behind the number bubbles */}
          <div
            className="absolute left-[27px] top-6 bottom-6 w-[2px] rounded-full"
            style={{
              background:
                "linear-gradient(to bottom, #3b82f6 0%, #6366f1 25%, #8b5cf6 55%, #a855f7 80%, #d946ef 100%)",
              opacity: 0.5,
            }}
          />

          <div className="space-y-2.5">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.07, duration: 0.45, ease: "easeOut" }}
                  className="relative flex items-stretch gap-5 group"
                >
                  {/* Number bubble */}
                  <div className="relative flex-shrink-0 w-[56px] flex items-start justify-center pt-1">
                    <div
                      className="relative w-[44px] h-[44px] rounded-full flex items-center justify-center font-extrabold text-white text-[15px] shadow-lg ring-4 ring-slate-50"
                      style={{
                        background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}dd 100%)`,
                        boxShadow: `0 8px 20px -6px ${s.color}55, 0 0 0 1px ${s.color}40 inset`,
                      }}
                    >
                      {s.number}
                    </div>
                  </div>

                  {/* Card */}
                  <motion.div
                    whileHover={{ y: -1 }}
                    className="flex-1 bg-white/90 backdrop-blur-sm rounded-xl border border-slate-200/80 px-5 py-3.5 flex items-center gap-4 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] hover:shadow-[0_12px_32px_-12px_rgba(15,23,42,0.14)] transition-shadow"
                    style={{
                      borderLeftWidth: "3px",
                      borderLeftColor: s.color,
                    }}
                  >
                    {/* Icon chip */}
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${s.color}18, ${s.color}08)`,
                        color: s.color,
                      }}
                    >
                      <Icon className="w-[18px] h-[18px]" strokeWidth={2.3} />
                    </div>

                    {/* Title + description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-[15px] lg:text-[16px] font-bold text-slate-900 leading-tight">
                          {s.title}
                        </h3>
                        <span
                          className="text-[9.5px] font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 rounded"
                          style={{
                            color: s.color,
                            backgroundColor: `${s.color}12`,
                          }}
                        >
                          {s.today ? "Today" : `Step ${s.number}`}
                        </span>
                      </div>
                      <p className="text-[12.5px] lg:text-[13px] text-slate-600 leading-[1.5]">
                        {s.text}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5 }}
          className="mt-6 text-center flex-shrink-0"
        >
          <p className="text-[12.5px] lg:text-[13px] text-slate-500 max-w-2xl mx-auto leading-relaxed">
            By the end, you've seen the product, used the product, and heard from peers running it.{" "}
            <span className="text-slate-800 font-semibold">No surprises. No pressure.</span>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default DeRiskProcessSlide;
